import {
  INTERVAL_LABELS,
  RANGE_LABELS,
  describeRange,
  type CustomRange,
  type IntervalKey,
  type RangeKey,
  type ResolvedRange,
} from '../time';

interface RangeControlsProps {
  rangeKey: RangeKey;
  onRangeKeyChange: (key: RangeKey) => void;
  custom: CustomRange;
  onCustomChange: (custom: CustomRange) => void;
  intervalKey: IntervalKey;
  onIntervalKeyChange: (key: IntervalKey) => void;
  /** The window the presets resolved to, or null while a custom range is incomplete. */
  range: ResolvedRange | null;
  onRefresh: () => void;
}

/**
 * Range + bucket controls shared by both Cost pages: the rolling presets, the
 * last complete billing cycle, an explicit date pair, the bucket width and a
 * refresh. The date inputs only appear under the `custom` preset so the common
 * case stays a single row of buttons.
 */
export function RangeControls({
  rangeKey,
  onRangeKeyChange,
  custom,
  onCustomChange,
  intervalKey,
  onIntervalKeyChange,
  range,
  onRefresh,
}: RangeControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
        {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => onRangeKeyChange(key)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              rangeKey === key
                ? 'bg-indigo-50 text-indigo-600'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {RANGE_LABELS[key]}
          </button>
        ))}
      </div>

      {rangeKey === 'custom' && (
        <div className="inline-flex items-center gap-1.5">
          <input
            type="date"
            value={custom.from}
            max={custom.to || undefined}
            onChange={(e) => onCustomChange({ ...custom, from: e.target.value })}
            aria-label="From date"
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            value={custom.to}
            min={custom.from || undefined}
            onChange={(e) => onCustomChange({ ...custom, to: e.target.value })}
            aria-label="To date"
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      )}

      {range && rangeKey !== 'custom' && (
        <span className="text-[11px] text-slate-400 tabular-nums">{describeRange(range)}</span>
      )}

      <select
        value={intervalKey}
        onChange={(e) => onIntervalKeyChange(e.target.value as IntervalKey)}
        className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-primary-500"
        title="Bucket interval"
        aria-label="Bucket interval"
      >
        {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
          <option key={key} value={key}>
            {INTERVAL_LABELS[key]}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={onRefresh}
        title="Refresh"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
        </svg>
        Refresh
      </button>
    </div>
  );
}
